/**
 * Refetches cached FHIR data when the UI language changes. Coded content
 * (med classes, roles, lab names) is translated server-side by the request's
 * Accept-Language, so a switch only takes effect once queries re-run.
 * MUST render inside <Refine> so it reads Refine's react-query client.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useQueryClient } from "@tanstack/react-query";

/**
 * Subscribes to i18next's `languageChanged` and invalidates every query so the
 * backend re-translates coded displays under the new `Accept-Language`.
 *
 * @returns null; this component renders nothing.
 */
export function LocaleChangeInvalidator(): null {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const invalidateAll = () => {
      // No query-key filter: every cached FHIR resource may carry coded displays.
      queryClient.invalidateQueries();
    };

    i18n.on("languageChanged", invalidateAll);
    return () => {
      i18n.off("languageChanged", invalidateAll);
    };
  }, [i18n, queryClient]);

  return null;
}
